import { inject, Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { IRoutineTemplate } from '../../interfaces/routine.interfaces';
import { GetAllRoutineTemplates } from '../../graphql/routine.queries';
import { map } from 'rxjs';
import { DaysOfWeekType } from '@app/pages/cmms/interfaces/scheduled-value.interface';
import {
	CreateRoutineTemplate,
	DeleteRoutineTemplate,
	EditRoutineTemplate,
} from '../../graphql/routine.mutations';
import { getRoutineTemplates } from '../state/routines.actions';
import {
	OpenSidebarEventTypes,
	RightSidebarRoutes,
} from '@app/layout/new-right-sidebar/types';
import { Store } from '@ngrx/store';
import { NewRightSidebarService } from '@app/layout/new-right-sidebar/services/new-right-sidebar/new-right-sidebar.service';

@Injectable({
	providedIn: 'root',
})
export class RoutineService {
	private readonly apollo = inject(Apollo);
	private readonly store$ = inject(Store);
	private readonly rightSidebarService = inject(NewRightSidebarService);

	getRoutineTemplates(pk: string = null) {
		return this.apollo
			.query<{ routineTemplates: IRoutineTemplate[] }>({
				query: GetAllRoutineTemplates,
				fetchPolicy: 'network-only',
				variables: { pk: pk },
			})
			.pipe(
				map(({ data }) => {
					return data.routineTemplates.map(template => ({
						...template,
						selectedDays: template.selectedDays.map(
							day => day.toLowerCase() as DaysOfWeekType
						),
					}));
				})
			);
	}

	editRoutineTemplate(routineTemplate: Partial<IRoutineTemplate>) {
		return this.apollo
			.mutate<{ routineTemplate: IRoutineTemplate }>({
				mutation: EditRoutineTemplate,
				fetchPolicy: 'network-only',
				variables: { ...routineTemplate },
			})
			.pipe(
				map(({ data }) => {
					return data;
				})
			);
	}

	deleteRoutineTemplate(pk: string) {
		return this.apollo
			.mutate<{ deleteRoutineTemplate: { success: boolean } }>({
				mutation: DeleteRoutineTemplate,
				variables: { pk },
			})
			.pipe(map(response => response.data?.deleteRoutineTemplate.success));
	}

	createRoutineTemplate(createRoutineVariables: Partial<IRoutineTemplate>) {
		this.apollo
			.mutate<{
				createRoutineTemplate: { routineTemplate: IRoutineTemplate; success: boolean };
			}>({
				mutation: CreateRoutineTemplate,
				variables: {
					name: createRoutineVariables.name,
					category: createRoutineVariables.category,
					priority: createRoutineVariables.priority,
					interval: createRoutineVariables.interval,
					period: createRoutineVariables.period,
					excludeWeekends: createRoutineVariables.excludeWeekends,
					monthSetup: createRoutineVariables.monthSetup,
					selectedDays: createRoutineVariables.selectedDays,
					description: createRoutineVariables.description,
					checklistQuestions: [],
				},
			})
			.pipe(map(response => response.data?.createRoutineTemplate.routineTemplate.id))
			.subscribe((routineTemplateId: string) => {
				this.store$.dispatch(getRoutineTemplates({ silent: true }));
				this.rightSidebarService.openAndNavigate({
					type: OpenSidebarEventTypes.OpenRoutineTemplate,
					route: RightSidebarRoutes.RoutineTemplate,
					pk: routineTemplateId,
				});
			});
	}
}
